import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarRateIcon from '@mui/icons-material/StarRate';
import {
  alpha,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { Dispatch, FC, SetStateAction, useContext } from 'react';

import { ApplicationStatus, DatePeriod } from '@features/applications';
import {
  applicationStatuses as baseApplicationStatuses,
  interviewTypeCTAs,
} from '@features/applications/components/ApplicationsListFilters/contants';
import { getHiringStatusButtonColor } from '@features/applications/components/ApplicationsListFilters/utils';
import {
  lightPurple,
  primaryGreen,
  primaryPurple,
  primaryRed,
  white,
  yellow,
} from '@styles/colors';

import TagsWotcFilters from '../TagsWotcFilters/TagsWotcFilters';
import { useGetInterviewCounts } from '@features/applications/hooks/useGetInterviewCounts';
import { authContext } from '@staffedupusa/shared-components';

type ApplicationsListFiltersProps = {
  selectedStatus: ApplicationStatus | string;
  setSelectedStatus: Dispatch<SetStateAction<ApplicationStatus | string>>;
  rating: 'All' | number;
  setRating: Dispatch<SetStateAction<'All' | number>>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<'All' | string>>;
  disableButtons: boolean;
  selectedDatePeriod: DatePeriod;
  setSelectedDatePeriod: Dispatch<SetStateAction<DatePeriod>>;
  openCustomDateModal: () => void;
  handlePageNo: (newPageNo: number) => void;
  isWOTCOnly: boolean;
  setIsWOTCOnly: Dispatch<SetStateAction<boolean>>;
  handleTagsChange: (event: {
    target: { value: React.SetStateAction<string> };
  }) => void;
  selectedCompanyId?: string | number;
  selectedTag: string;
  selectedInterviewType?: string;
  handleSelectedInterviewTypeChange?: (type: string) => void;
};

const ApplicationsListFilters: FC<ApplicationsListFiltersProps> = ({
  selectedStatus,
  setSelectedStatus,
  rating,
  selectedTag,
  setRating,
  isWOTCOnly,
  setIsWOTCOnly,
  disableButtons,
  selectedDatePeriod,
  setSelectedDatePeriod,
  openCustomDateModal,
  handlePageNo,
  selectedCompanyId,
  handleTagsChange,
  selectedInterviewType,
  handleSelectedInterviewTypeChange,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData?.user?.company;
  const theme = useTheme();
  const isMdOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const { interviewCount } = useGetInterviewCounts({
    enabled: true,
    selectedCompanyId: selectedCompanyId,
  });

  const applicationStatuses = React.useMemo(() => {
    const statuses = [...baseApplicationStatuses];
    if ((isCompany && (authData?.user?.company?.plan === 2 || authData?.user?.company?.enterprise_company &&
      import.meta.env.VITE_AUTOMATION_ACCESS_ENTERPRISE_IDS?.includes(
        authData?.user?.company?.enterprise_company?.id,
      ))) || !isCompany) {
      const newStatus = {
        status: 'Onboarding Pending',
        buttonText: 'Onboarding',
      };
      //@ts-ignore
      statuses.splice(5, 0, newStatus);
    }
    return statuses;
  }, [isCompany, authData?.user?.company]);
  const colorMap = {
    yellow: yellow,
    green: primaryGreen,
    purple: primaryPurple,
    lightPurple: lightPurple,
    dark: '#424242',
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
  };

  const getColorValue = (colorName: keyof typeof colorMap) => {
    return colorMap[colorName] || yellow;
  };

  const renderApplicationStatusButtons = () => {
    if (isMdOrSmaller) {
      return (
        <FormControl
          size="small"
          variant="outlined"
          disabled={disableButtons}
          fullWidth
          sx={{ borderRadius: 5 }}
        >
          <InputLabel id="rating-statuses">Filter by status</InputLabel>
          <Select
            size="small"
            label="Filter by Date"
            labelId="rating-statuses"
            value={selectedStatus}
            onChange={(e) => {
              handlePageNo(1);
              setSelectedStatus(e.target.value as ApplicationStatus);
            }}
            MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
          >
            {applicationStatuses.map((item) => (
              <MenuItem key={item.status} value={item.status}>
                {item.buttonText || item.status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <>
        <Stack
          spacing={1}
          pb={2}
          direction="row"
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            variant={selectedStatus === 'All' ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            onClick={() => {
              handlePageNo(1);
              setSelectedStatus('All');
            }}
            disabled={disableButtons}
            style={{ fontSize: '14px' }}
          >
            All
          </Button>
          {applicationStatuses.map((item) => {
            const isSelected = item.status === selectedStatus;
            const colorName = getHiringStatusButtonColor(item.status);
            const color = getColorValue(colorName);

            return (
              <Button
                key={item.status}
                variant={isSelected ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  handlePageNo(1);
                  setSelectedStatus(item.status);
                  item.status === 'interview process'
                    ? handleSelectedInterviewTypeChange?.('0')
                    : handleSelectedInterviewTypeChange?.('');
                }}
                sx={{
                  px: { xs: 1 },
                  flexGrow: 1,
                  backgroundColor: isSelected
                    ? alpha(color, 0.2)
                    : 'transparent',
                  color: color,
                  border: isSelected ? 'none' : `1px solid ${color}`,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? alpha(color, 0.2)
                      : 'transparent',
                    border: isSelected ? 'none' : `1px solid ${color}`,
                  },
                  boxShadow: isSelected ? 'none' : '0px 0px 0px 0px',
                }}
                disabled={disableButtons}
              >
                {item.status === 'interview process' ? (
                  <>
                    interviews&nbsp;
                    {interviewCount?.count && interviewCount?.count > 0 ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: primaryRed,
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                      >
                        {interviewCount?.count}
                      </Box>
                    ) : null}
                  </>
                ) : item.status === 'pending job offers' ? (
                  'offers'
                ) : (
                  item.buttonText || item.status
                )}
              </Button>
            );
          })}
        </Stack>
        {selectedStatus === 'interview process' && (
          <Stack
            spacing={1}
            pb={2}
            direction="row"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ width: '100%' }}
          >
            {interviewTypeCTAs.map(({ label, value, colorToCopy }) => {
              const isSelected = selectedInterviewType === value;
              const colorName = getHiringStatusButtonColor(colorToCopy);
              const color = getColorValue(colorName);
              return (
                <Button
                  key={value}
                  variant={isSelected ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    handlePageNo(1);
                    handleSelectedInterviewTypeChange?.(value);
                  }}
                  disabled={disableButtons}
                  sx={{
                    px: { xs: 1 },
                    flexGrow: 1,
                    backgroundColor: isSelected
                      ? alpha(color, 0.2)
                      : 'transparent',
                    color: color,
                    border: isSelected ? 'none' : `1px solid ${color}`,
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? alpha(color, 0.2)
                        : 'transparent',
                      border: isSelected ? 'none' : `1px solid ${color}`,
                    },
                    boxShadow: isSelected ? 'none' : '0px 0px 0px 0px',
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Stack>
        )}
      </>
    );
  };

  const renderApplicationRatingButtons = () => {
    if (isMdOrSmaller) {
      return (
        <FormControl
          size="small"
          variant="outlined"
          disabled={disableButtons}
          fullWidth
        >
          <InputLabel id="rating-statuses">Filter by Rating</InputLabel>
          <Select
            size="small"
            label="Filter by Rating"
            labelId="rating-statuses"
            value={rating}
            onChange={(e) => {
              handlePageNo(1);
              setRating(e.target.value as 'All' | number);
            }}
            inputProps={{
              sx: { color: rating === 'All' ? 'text.primary' : yellow },
            }}
            style={{ fontSize: '14px' }}
            MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
          >
            <MenuItem value="All">All</MenuItem>
            {[1, 2, 3, 4, 5].map((count) => (
              <MenuItem
                key={count}
                value={count}
                sx={{ color: yellow }}
                style={{ minWidth: 'unset' }}
              >
                {Array.from({ length: count }, (_, i) => (
                  <StarRateIcon key={i} style={{ fontSize: '18px' }} />
                ))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <Stack
        spacing={1}
        direction="row"
        useFlexGap
        sx={{
          justifyContent: { xs: 'center', md: 'flex-start' },
        }}
      >
        <Button
          variant={rating === 'All' ? 'contained' : 'outlined'}
          size="small"
          color="secondary"
          onClick={() => {
            handlePageNo(1);
            setRating('All');
          }}
          disabled={disableButtons}
          style={{ fontSize: '14px' }}
        >
          All
        </Button>
        {[1, 2, 3, 4, 5].map((count) => {
          const renderContent = () => {
            if (count <= 2) {
              return Array.from({ length: count }, (_, i) => (
                <StarRateIcon key={i} style={{ fontSize: '18px' }} />
              ));
            } else {
              return (
                <>
                  <span style={{ marginRight: '4px' }}>{count}</span>
                  <StarRateIcon style={{ fontSize: '18px' }} />
                </>
              );
            }
          };

          return (
            <Button
              key={count}
              variant={rating === count ? 'contained' : 'outlined'}
              size="small"
              color="secondary"
              sx={{ color: yellow }}
              onClick={() => {
                handlePageNo(1);
                setRating(count);
              }}
              disabled={disableButtons}
              style={{ minWidth: 'unset' }}
            >
              {renderContent()}
            </Button>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack bgcolor={white} borderRadius="8px" p={3} mt={isCompany ? 0 : 3} spacing={2}>
      {renderApplicationStatusButtons()}
      <Divider />
      <Stack
        pt={2}
        spacing={2}
        direction={{ xs: 'column', lg: 'row' }}
        alignItems="center"
        overflow="auto"
      >
        {renderApplicationRatingButtons()}
        <Stack
          sx={{ borderRadius: 5, alignSelf: 'start' }}
          spacing={2}
          alignItems="center"
          direction={{ xs: 'column', md: 'row' }}
          width="100%"
        >
          <FormControl
            size="small"
            variant="outlined"
            disabled={disableButtons}
            fullWidth
            sx={{
              borderRadius: 5,
              width: { md: '140px !important', sm: '100%' },
            }}
          >
            <InputLabel id="date-filter">Filter by Date</InputLabel>
            <Select
              size="small"
              label="Filter by Date"
              labelId="date-filter"
              value={selectedDatePeriod}
              onChange={(e) => {
                {
                  handlePageNo(1);
                  setSelectedDatePeriod(e.target.value as DatePeriod);
                }
              }}
              sx={{ borderRadius: 5, fontSize: '14px' }}
              MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
            >
              <MenuItem value="ALL">All time</MenuItem>
              <MenuItem value="TODAY">Today</MenuItem>
              <MenuItem value="YESTERDAY">Yesterday</MenuItem>
              <MenuItem value="WEEK">7 days</MenuItem>
              <MenuItem value="MONTH">30 days</MenuItem>
              <MenuItem value="custom date">Custom date</MenuItem>
            </Select>
          </FormControl>

          {selectedDatePeriod === 'custom date' && (
            <IconButton onClick={openCustomDateModal}>
              <CalendarMonthIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
          <TagsWotcFilters
            disableButtons={disableButtons}
            isWOTCOnly={isWOTCOnly}
            onWotcChange={() => {
              handlePageNo(1);
              setIsWOTCOnly(!isWOTCOnly);
            }}
            onTagsChange={(e) => {
              handlePageNo(1);
              handleTagsChange(e);
            }}
            selectedTag={selectedTag}
            selectedCompanyId={selectedCompanyId}
            expended={selectedDatePeriod === 'custom date' ? true : false}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ApplicationsListFilters;
